import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, map, distinctUntilChanged } from 'rxjs';
import { CommonService } from './common.service';

@Injectable({ providedIn: 'root' })
export class DashboardOverrideService {
  private static readonly DRILL_DOWN_KEY = 'DRILL_DOWN';

  private readonly overrides$ = new BehaviorSubject<Map<string, string | null>>(new Map());
  readonly isAnyOverridden$: Observable<boolean>;

  private readonly lostAccessWidgets = new Set<string>();
  private readonly lostAccessOnceSubject$ = new Subject<true>();
  readonly lostAccessOnce$: Observable<true> = this.lostAccessOnceSubject$.asObservable();

  constructor(private commonService: CommonService) {
    this.isAnyOverridden$ = combineLatest([
      this.overrides$,
      this.commonService.selectedOrgEmitter,
    ]).pipe(
      map(([m, topOrg]) => {
        for (const v of m.values()) {
          if (v != null && v !== topOrg?.id) return true;
        }
        return false;
      }),
      distinctUntilChanged()
    );
  }

  reportLostAccess(widgetId: string): void {
    if (this.lostAccessWidgets.size === 0) {
      this.lostAccessOnceSubject$.next(true);
    }
    this.lostAccessWidgets.add(widgetId);
  }

  setOverride(widgetId: string, orgLevelId: string | null): void {
    const next = new Map(this.overrides$.value);
    next.set(widgetId, orgLevelId);
    this.overrides$.next(next);
  }

  clearWidget(widgetId: string): void {
    const next = new Map(this.overrides$.value);
    next.delete(widgetId);
    this.overrides$.next(next);
  }

  clearAll(): void {
    this.overrides$.next(new Map());
    this.lostAccessWidgets.clear();
  }

  setDrillDownOverride(orgLevelId: string | null): void {
    this.setOverride(DashboardOverrideService.DRILL_DOWN_KEY, orgLevelId);
  }

  clearDrillDownOverride(): void {
    this.clearWidget(DashboardOverrideService.DRILL_DOWN_KEY);
  }
}
